import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { capitalize } from "@/utils/capitalize";

interface Session {
  id: number;
  sujet: string;
  notes: string;
  status: "PREVUE" | "REALISEE" | "EN_COURS";
  order: number;
  sessionDate: string;
  fiable: boolean;
  type: string[];
  patientId: number;
  sessionType: "BILAN" | "REEDUCATION";
}

// interface TableSessionsListProps {
//   sessionsList: Session[];
// }

const TableSessionsList: React.FC = () => {
  const [sessionsPrevues, setSessionsPrevues] = useState<Session[]>([]);
  const [sessionsEnCours, setSessionsEnCours] = useState<Session[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch sessions list
    axios
      .get("/patient/sessions")
      .then((response) => {
        const sessions = response.data;
        // Séparer les séances prévues et en cours
        const prevues = sessions.filter((session: Session) => session.status === "PREVUE");
        const enCours = sessions.filter((session: Session) => session.status === "EN_COURS");
        
        setSessionsPrevues(prevues);
        setSessionsEnCours(enCours);
      })
      .catch((error) => {
        console.error("Error fetching sessions list: ", error);
      });
  }, []);

  const handleSessionStart = (sessionId: number, sessionType: "BILAN" | "REEDUCATION") => {
    if (sessionType === "BILAN") {
      navigate(`/modules/patient/${sessionId}/bilan`);
    } else if (sessionType === "REEDUCATION") {
      navigate(`/modules/patient/${sessionId}/reeducation`);
    }
  };

  const getButtonForSession = (session: Session, isFirst: boolean) => {
    if (session.status === "EN_COURS") {
      return (
        <Button
          variant="outline"
          className="bg-orange-400 hover:bg-orange-500 text-white"
          onClick={() => handleSessionStart(session.id, session.sessionType)}
        >
          Reprendre la séance
        </Button>
      );
    } else if (session.status === "PREVUE" && isFirst) {
      return (
        <Button
          variant="outline"
          className="bg-green-400 hover:bg-green-500"
          onClick={() => handleSessionStart(session.id, session.sessionType)}
        >
          Démarrer
        </Button>
      );
    }
    return null;
  };

  return (
    <Card className="w-5/6 h-5/6">
      <CardHeader className="flex flex-row items-center">
        <div className="mx-auto">
          <CardTitle>Mes Séances</CardTitle>
          <CardDescription>
            {sessionsEnCours.length > 0 
              ? "Séances en cours et à réaliser" 
              : "Liste de mes séances à réaliser"
            }
          </CardDescription>
        </div>
        <div className="w-1/4"></div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Ordre</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>Exercice(s)</TableHead>
              <TableHead>Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="text-left">
            {/* Afficher d'abord les séances en cours */}
            {sessionsEnCours
              .sort((a, b) => a.order - b.order)
              .map((session) => (
                <TableRow
                  key={session.id}
                  className="cursor-pointer hover:bg-gray-100 bg-orange-50"
                >
                  <TableCell>{session.order}</TableCell>
                  <TableCell>
                    <span className="inline-block bg-orange-500 text-white rounded px-2 py-1 text-xs">
                      En cours
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="relative group">
                      {/* Vue par défaut : premier exercice et compteur d'exercices supplémentaires */}
                      <div className="flex items-center">
                        <span className="inline-block border border-gray-300 rounded px-2 py-1 mr-1">
                          {capitalize(session.type[0])}
                        </span>
                        {session.type.length > 1 && (
                          <span className="inline-block border border-gray-300 rounded px-2 py-1">
                            +{session.type.length - 1}
                          </span>
                        )}
                      </div>
                      {/* Popup en hover : affichage complet en grille avec 3 colonnes auto */}
                      <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 p-2 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-50">
                        <div className="grid grid-cols-[repeat(3,auto)] gap-2">
                          {session.type.map((type, index) => (
                            <span
                              key={index}
                              className="block border border-gray-300 rounded px-2 py-1 text-sm"
                            >
                              {capitalize(type)}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {getButtonForSession(session, false)}
                  </TableCell>
                </TableRow>
              ))}
            
            {/* Puis afficher les séances prévues */}
            {sessionsPrevues
              .sort((a, b) => a.order - b.order)
              .map((session, index) => (
                <TableRow
                  key={session.id}
                  className="cursor-pointer hover:bg-gray-100"
                >
                  <TableCell>{session.order}</TableCell>
                  <TableCell>
                    <span className="inline-block bg-blue-500 text-white rounded px-2 py-1 text-xs">
                      Prévue
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="relative group">
                      {/* Vue par défaut : premier exercice et compteur d'exercices supplémentaires */}
                      <div className="flex items-center">
                        <span className="inline-block border border-gray-300 rounded px-2 py-1 mr-1">
                          {capitalize(session.type[0])}
                        </span>
                        {session.type.length > 1 && (
                          <span className="inline-block border border-gray-300 rounded px-2 py-1">
                            +{session.type.length - 1}
                          </span>
                        )}
                      </div>
                      {/* Popup en hover : affichage complet en grille avec 3 colonnes auto */}
                      <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 p-2 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-50">
                        <div className="grid grid-cols-[repeat(3,auto)] gap-2">
                          {session.type.map((type, index) => (
                            <span
                              key={index}
                              className="block border border-gray-300 rounded px-2 py-1 text-sm"
                            >
                              {capitalize(type)}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {getButtonForSession(session, index === 0 && sessionsEnCours.length === 0)}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default TableSessionsList;
